import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEMO_MONTH = "2026-06";
const CUSTOM_STAFFING_SCENARIO = "custom-staffing";
const SHIFT_DISPLAY = { P1: "A1", P2: "A2", P3: "A3", P4: "A4", P5: "A5" };
const SPECIAL_SHIFT_DISPLAY = { V: "Visit", Z: "Zone", SV: "Post-Visit", AUT: "Emergency Vehicle", AUTN: "Night Emergency Vehicle" };
const TIME_OFF_DISPLAY = { FE: "Vacation", C: "Training", MA: "Sick Leave", PE: "Personal Leave", R: "Rest" };
const SHIFT_GROUPS = [
  { label: "Morning", shifts: ["M1", "M2", "M3", "M4", "M5"] },
  { label: "Afternoon", shifts: ["A1", "A2", "A3", "A4", "A5"] },
  { label: "Night", shifts: ["N1", "N2", "N3"] },
  { label: "Special", shifts: ["V", "Z", "SV"] },
  { label: "Monthly", shifts: ["AUT", "AUTN"] },
];

function getApiBaseUrl() {
  if (process.env.REACT_APP_MEDISHIFT_DEMO_API_BASE_URL) {
    return process.env.REACT_APP_MEDISHIFT_DEMO_API_BASE_URL;
  }
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)) {
    return "http://localhost:5000";
  }
  return "/api";
}

function displayShift(shift) {
  return SHIFT_DISPLAY[shift] || SPECIAL_SHIFT_DISPLAY[shift] || TIME_OFF_DISPLAY[shift] || shift || "";
}

function shiftClassName(shift) {
  const code = String(shift || "").trim().toUpperCase();
  if (/^M/.test(code)) return "shift-morning";
  if (/^A|^P/.test(code)) return "shift-afternoon";
  if (/^N|AUTN/.test(code)) return "shift-night";
  if (/^AUT|^V$|^Z$|^SV$/.test(code)) return "shift-special";
  if (TIME_OFF_DISPLAY[code]) return "shift-timeoff";
  return "";
}

function countFromValidation(validation, key) {
  if (!validation) return 0;
  if (validation.counts && Number.isFinite(validation.counts[key])) return validation.counts[key];
  if (key === "hard") return validation.errors?.length || 0;
  if (key === "soft") return validation.warnings?.length || 0;
  if (key === "unsupported") return validation.unsupported?.length || 0;
  return 0;
}

function formatDayList(days) {
  if (!Array.isArray(days) || days.length === 0) return "None";
  return days.join(", ");
}

function formatTimeOff(timeOff = {}) {
  const entries = Object.entries(timeOff).filter(([, days]) => Array.isArray(days) && days.length > 0);
  if (entries.length === 0) return "None";
  return entries.map(([code, days]) => `${displayShift(code)} days ${formatDayList(days)}`).join("; ");
}

function formatRequiredShiftDays(requiredShiftDays = {}) {
  const entries = Object.entries(requiredShiftDays).filter(([, days]) => Array.isArray(days) && days.length > 0);
  if (entries.length === 0) return "None";
  return entries.map(([shift, days]) => `${displayShift(shift)} days ${formatDayList(days)}`).join("; ");
}

function groupAvailability(staff) {
  return SHIFT_GROUPS.map((group) => ({
    ...group,
    available: group.shifts.filter((shift) => staff.availability?.includes(shift)),
  }));
}

function formatShiftList(shifts) {
  if (!Array.isArray(shifts) || shifts.length === 0) return "None";
  return shifts.map(displayShift).join(", ");
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    return { error: "The demo returned an unreadable response." };
  }
}

function StaffSelectionEditor({ fixture, selectedStaffIds, onAddStaff, onRemoveStaff }) {
  const customization = fixture?.customization;
  const staffPool = customization?.staffPool || [];
  const selectedSet = new Set(selectedStaffIds || []);
  const selectedStaff = staffPool.filter((staff) => selectedSet.has(staff.id));
  const availableStaff = staffPool.filter((staff) => !selectedSet.has(staff.id));
  const [staffToAdd, setStaffToAdd] = useState("");

  useEffect(() => {
    if (!availableStaff.some((staff) => staff.id === staffToAdd)) {
      setStaffToAdd(availableStaff[0]?.id || "");
    }
  }, [availableStaff, staffToAdd]);

  if (!customization?.enabled) return null;

  const minDoctors = customization.minDoctors || selectedStaff.length;
  const maxDoctors = customization.maxDoctors || staffPool.length;
  const canAdd = Boolean(staffToAdd) && selectedStaff.length < maxDoctors;
  const canRemove = selectedStaff.length > minDoctors;

  return (
    <div className="setup-field custom-staffing-field">
      <div className="custom-staffing-summary">
        <span>Staffing</span>
        <span>{selectedStaff.length} doctors</span>
      </div>
      <div className="custom-staffing-controls">
        <label htmlFor="custom-staff-add">Add doctor</label>
        <select id="custom-staff-add" value={staffToAdd} onChange={(event) => setStaffToAdd(event.target.value)}>
          {availableStaff.map((staff) => (
            <option key={staff.id} value={staff.id}>{staff.name}</option>
          ))}
        </select>
        <button type="button" className="secondary-demo-btn" disabled={!canAdd} onClick={() => onAddStaff(staffToAdd)}>
          Add doctor
        </button>
      </div>
      <div className="selected-staff-list" role="list" aria-label="Selected doctors">
        {selectedStaff.map((staff) => (
          <span key={staff.id} className="selected-staff-chip" role="listitem">
            <span>{staff.name}</span>
            <button type="button" disabled={!canRemove} onClick={() => onRemoveStaff(staff.id)} aria-label={`Remove ${staff.name}`}>
              Remove
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function DemoSetup({
  fixture,
  scenario,
  customSelectedStaffIds,
  onScenarioChange,
  onAddStaff,
  onRemoveStaff,
  onGenerate,
  onExport,
  canExport,
  isGenerating,
  isExporting,
  hasGeneratedSchedule,
}) {
  const scenarioDetails = fixture?.availableScenarios?.find((option) => option.id === scenario);

  return (
    <section className="medishift-setup-grid">
      <div className="medishift-panel demo-control-panel">
        <div className="medishift-panel-heading">
          <h2>Demo Setup</h2>
          <span>{fixture?.staff?.length || 0} doctors</span>
        </div>
        <div className="setup-body">
          <div className="setup-field">
            <label htmlFor="demo-month">Selected month</label>
            <input id="demo-month" type="month" value={DEMO_MONTH} readOnly aria-readonly="true" />
          </div>
          <div className="setup-field setup-scenario-field">
            <span>Scenario</span>
            <div className="scenario-tabs" role="tablist" aria-label="Demo scenario">
              {(fixture?.availableScenarios || []).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={scenario === option.id ? "active" : ""}
                  onClick={() => onScenarioChange(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {scenarioDetails && <p className="scenario-description">{scenarioDetails.description}</p>}
          </div>
          <div className="setup-actions">
            <button type="button" className="primary-demo-btn" onClick={onGenerate} disabled={isGenerating}>
              {isGenerating ? "Generating" : hasGeneratedSchedule ? "Generate Different Schedule" : "Generate Schedule"}
            </button>
            <button type="button" className="secondary-demo-btn" onClick={onExport} disabled={!canExport || isExporting}>
              {isExporting ? "Exporting" : "Export Excel"}
            </button>
          </div>
          <StaffSelectionEditor
            fixture={fixture}
            selectedStaffIds={customSelectedStaffIds}
            onAddStaff={onAddStaff}
            onRemoveStaff={onRemoveStaff}
          />
        </div>
      </div>

      <div className="medishift-panel coverage-panel">
        <div className="medishift-panel-heading">
          <h2>Daily Coverage</h2>
          <span>{fixture?.dailyCoverage?.count || 0} shifts per day</span>
        </div>
        <div className="coverage-groups">
          {SHIFT_GROUPS.map((group) => (
            <div key={group.label} className="coverage-group">
              <strong>{group.label}</strong>
              <span>{formatShiftList(group.shifts)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AvailabilityMatrix({ fixture, resetKey }) {
  const staffRows = fixture?.staff || [];
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [resetKey]);

  return (
    <section className="medishift-panel medishift-availability">
      <div className="medishift-panel-heading">
        <h2>Doctor Availability</h2>
        <span>{staffRows.length} doctors</span>
      </div>
      <div ref={scrollRef} className="availability-scroll" role="region" aria-label="Doctor availability" tabIndex="0">
        <table className="availability-table">
          <thead>
            <tr>
              <th>Doctor</th>
              <th>Eligible shifts</th>
              <th>Required shift days</th>
              <th>Time off</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {staffRows.map((staff) => (
              <tr key={staff.id}>
                <th>{staff.name}</th>
                <td>
                  <div className="availability-chip-grid">
                    {groupAvailability(staff).map((group) => (
                      <span key={group.label} className="availability-chip">
                        <strong>{group.label}</strong>
                        {formatShiftList(group.available)}
                      </span>
                    ))}
                  </div>
                </td>
                <td>{formatRequiredShiftDays(staff.requiredShiftDays)}</td>
                <td>{formatTimeOff(staff.timeOff)}</td>
                <td>{staff.notes?.[0] || "Available for all listed daily shifts."}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ValidationPanel({ validation }) {
  const items = validation?.constraintResults || [];
  const visibleItems = items.filter((item) => item.status !== "passed");

  return (
    <section className="medishift-panel medishift-validation" aria-live="polite">
      <div className="medishift-panel-heading">
        <h2>Validation</h2>
        <span className={`status-pill status-${validation?.status || "idle"}`}>
          {validation?.validation_status || "Not generated"}
        </span>
      </div>
      <div className="validation-metrics">
        <div>
          <span>{countFromValidation(validation, "hard")}</span>
          <label>Hard</label>
        </div>
        <div>
          <span>{countFromValidation(validation, "soft")}</span>
          <label>Soft</label>
        </div>
      </div>
      <div className="validation-list">
        {visibleItems.length === 0 ? (
          <p>{validation?.status === "valid" ? "All shown constraints passed." : "Validation appears after generation."}</p>
        ) : (
          visibleItems.map((item, index) => (
            <div key={`${item.constraintId}-${index}`} className={`validation-item ${item.severity}`}>
              <strong>{item.name || item.constraintId}</strong>
              <span>{item.message}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function ScheduleTable({ result, onSwap, isEditing }) {
  const [draggedCell, setDraggedCell] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  const mouseDragRef = useRef(null);

  const hasSchedule = Boolean(result?.schedule && Array.isArray(result?.dates));
  const entries = hasSchedule ? Object.entries(result.schedule) : [];
  const canDrag = Boolean(onSwap && result?.success && !isEditing && hasSchedule);

  const cellKey = useCallback((staff, dayIndex) => `${staff}:${dayIndex}`, []);

  const clearMouseDrag = useCallback(() => {
    mouseDragRef.current = null;
    setDraggedCell(null);
    setDragTarget(null);
  }, []);

  const payloadFromElement = useCallback((element) => {
    const cell = element?.closest?.(".schedule-cell");
    if (!cell?.dataset?.staff || cell.dataset.dayIndex === undefined) return null;
    return {
      staff: cell.dataset.staff,
      dayIndex: Number(cell.dataset.dayIndex),
      shift: cell.dataset.shift || "",
    };
  }, []);

  const finishMouseDrag = useCallback(
    (to) => {
      const from = mouseDragRef.current;
      const shouldSwap = canDrag && from?.moved && to;
      clearMouseDrag();
      if (shouldSwap) onSwap({ staff: from.staff, dayIndex: from.dayIndex, shift: from.shift }, to);
    },
    [canDrag, clearMouseDrag, onSwap]
  );

  useEffect(() => {
    if (!canDrag) return undefined;

    function handleMouseMove(event) {
      const from = mouseDragRef.current;
      if (!from) return;
      const distance = Math.max(Math.abs(event.clientX - from.startX), Math.abs(event.clientY - from.startY));
      if (distance < 5) return;
      from.moved = true;
      setDraggedCell(from.key);
      const to = payloadFromElement(event.target);
      if (to) setDragTarget(cellKey(to.staff, to.dayIndex));
    }

    function handleMouseUp(event) {
      const targetElement = typeof document.elementFromPoint === "function" ? document.elementFromPoint(event.clientX, event.clientY) : event.target;
      finishMouseDrag(payloadFromElement(targetElement));
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [canDrag, cellKey, finishMouseDrag, payloadFromElement]);

  if (!hasSchedule) {
    return (
      <section className="medishift-panel medishift-empty-state">
        <h2>Schedule</h2>
        <p>The generated calendar appears here after the availability data is sent to the scheduler.</p>
      </section>
    );
  }

  function readDraggedCell(event) {
    try {
      return JSON.parse(event.dataTransfer.getData("text/plain"));
    } catch (error) {
      return null;
    }
  }

  return (
    <section className="medishift-panel medishift-schedule-panel">
      <div className="medishift-panel-heading">
        <h2>Schedule</h2>
        <span>{result.summary?.totalStaff || entries.length} doctors</span>
      </div>
      <div className="schedule-scroll" role="region" aria-label="Generated MediShift demo schedule" tabIndex="0">
        <table className="schedule-table">
          <thead>
            <tr>
              <th>Staff</th>
              {result.dates.map((dateInfo) => (
                <th key={dateInfo.date}>
                  <span>{dateInfo.day}</span>
                  <small>{dateInfo.dayName}</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(([name, shifts]) => (
              <tr key={name}>
                <th>{name}</th>
                {shifts.map((shift, index) => {
                  const key = cellKey(name, index);
                  return (
                    <td
                      key={`${name}-${index}`}
                      className={`${shiftClassName(shift)} schedule-cell ${draggedCell === key ? "dragging" : ""} ${dragTarget === key ? "drag-over" : ""}`}
                      draggable={false}
                      data-can-drag={canDrag ? "true" : "false"}
                      data-staff={name}
                      data-day-index={index}
                      data-shift={shift}
                      data-cell-key={key}
                      title="Drag to another doctor on the same day"
                      aria-label={`${name}, day ${result.dates[index]?.day}, ${displayShift(shift)}`}
                      onMouseDown={(event) => {
                        if (!canDrag || event.button !== 0) return;
                        mouseDragRef.current = {
                          staff: name,
                          dayIndex: index,
                          shift,
                          key,
                          startX: event.clientX,
                          startY: event.clientY,
                          moved: false,
                        };
                      }}
                      onMouseEnter={(event) => {
                        if (!mouseDragRef.current?.moved) return;
                        const to = payloadFromElement(event.currentTarget);
                        if (to) setDragTarget(cellKey(to.staff, to.dayIndex));
                      }}
                      onMouseUp={(event) => {
                        if (!mouseDragRef.current) return;
                        finishMouseDrag(payloadFromElement(event.currentTarget));
                      }}
                      onDragStart={(event) => {
                        if (!canDrag) return;
                        const payload = { staff: name, dayIndex: index, shift };
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", JSON.stringify(payload));
                        setDraggedCell(key);
                      }}
                      onDragEnd={() => {
                        setDraggedCell(null);
                        setDragTarget(null);
                      }}
                      onDragOver={(event) => {
                        if (!canDrag) return;
                        event.preventDefault();
                        setDragTarget(key);
                      }}
                      onDragLeave={() => setDragTarget((current) => (current === key ? null : current))}
                      onDrop={(event) => {
                        if (!canDrag) return;
                        event.preventDefault();
                        setDraggedCell(null);
                        setDragTarget(null);
                        mouseDragRef.current = null;
                        const from = readDraggedCell(event);
                        if (from) onSwap(from, { staff: name, dayIndex: index, shift });
                      }}
                    >
                      {displayShift(shift)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ChatEditor({ chatFeature, chatAvailable, result, chatMessage, setChatMessage, onSubmit, isChatting }) {
  return (
    <section className="medishift-panel medishift-chat">
      <div className="medishift-panel-heading">
        <h2>AI Edit</h2>
        <span>{chatFeature?.provider || "checking"}</span>
      </div>
      <form onSubmit={onSubmit}>
        <textarea
          value={chatMessage}
          onChange={(event) => setChatMessage(event.target.value)}
          rows="3"
          aria-label="AI edit request"
        />
        <button type="submit" className="primary-demo-btn" disabled={!chatAvailable || !result?.success || isChatting}>
          {isChatting ? "Applying" : "Apply Edit"}
        </button>
      </form>
      {!chatAvailable && (
        <p className="demo-muted">AI edits require server-side demo credentials in this environment.</p>
      )}
    </section>
  );
}

export default function MediShiftDemo() {
  const apiBaseUrl = useMemo(getApiBaseUrl, []);
  const [health, setHealth] = useState(null);
  const [fixture, setFixture] = useState(null);
  const [scenario, setScenario] = useState("soft-warning");
  const [customSelectedStaffIds, setCustomSelectedStaffIds] = useState([]);
  const [result, setResult] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState("Swap ED Doctor 03 on day 5 with ED Doctor 12");
  const [notice, setNotice] = useState("");
  const [generationVariant, setGenerationVariant] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadDemoStatus() {
      try {
        const [healthResponse, fixtureResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/medishift-demo/health`),
          fetch(`${apiBaseUrl}/medishift-demo/fixture?scenario=${encodeURIComponent(scenario)}`),
        ]);
        const healthPayload = await parseJsonResponse(healthResponse);
        const fixturePayload = await parseJsonResponse(fixtureResponse);
        if (!cancelled) {
          setHealth(healthPayload);
          setFixture(fixturePayload);
          if (!healthResponse.ok) setNotice(healthPayload.error || "The demo status route is unavailable.");
        }
      } catch (error) {
        if (!cancelled) setNotice("The demo backend is unavailable from this environment.");
      }
    }

    loadDemoStatus();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, scenario]);

  useEffect(() => {
    if (scenario !== CUSTOM_STAFFING_SCENARIO || !fixture?.customization?.selectedStaffIds?.length) return;
    setCustomSelectedStaffIds((currentIds) => {
      const staffPoolIds = new Set((fixture.customization.staffPool || []).map((staff) => staff.id));
      const currentSelectionIsValid =
        currentIds.length >= fixture.customization.minDoctors &&
        currentIds.length <= fixture.customization.maxDoctors &&
        currentIds.every((staffId) => staffPoolIds.has(staffId));
      return currentSelectionIsValid ? currentIds : fixture.customization.selectedStaffIds;
    });
  }, [fixture, scenario]);

  const activeCustomStaffIds = useMemo(() => {
    if (scenario !== CUSTOM_STAFFING_SCENARIO) return [];
    return customSelectedStaffIds.length ? customSelectedStaffIds : fixture?.customization?.selectedStaffIds || [];
  }, [customSelectedStaffIds, fixture, scenario]);

  const visibleFixture = useMemo(() => {
    if (!fixture || scenario !== CUSTOM_STAFFING_SCENARIO || !fixture.customization?.enabled) return fixture;
    const selectedSet = new Set(activeCustomStaffIds);
    return {
      ...fixture,
      staff: (fixture.customization.staffPool || []).filter((staff) => selectedSet.has(staff.id)),
    };
  }, [fixture, scenario, activeCustomStaffIds]);

  const availabilityResetKey = scenario === CUSTOM_STAFFING_SCENARIO ? `${scenario}:${activeCustomStaffIds.join("|")}` : scenario;

  const chatFeature = health?.features?.chat;
  const chatAvailable = Boolean(chatFeature?.available);
  const canExport = Boolean(result?.success && result?.schedule);
  const hasGeneratedSchedule = Boolean(result?.success && result?.schedule);

  async function generateSchedule() {
    setIsGenerating(true);
    setNotice("");
    const nextVariant = hasGeneratedSchedule ? generationVariant + 1 : 0;
    const requestParams = new URLSearchParams({ month: DEMO_MONTH, scenario, variant: String(nextVariant) });
    if (scenario === CUSTOM_STAFFING_SCENARIO) requestParams.set("selectedStaffIds", activeCustomStaffIds.join(","));
    try {
      const response = await fetch(`${apiBaseUrl}/medishift-demo/generate-schedule?${requestParams.toString()}`);
      const payload = await parseJsonResponse(response);
      setResult(payload);
      if (payload.success) {
        setGenerationVariant(Number.isInteger(payload.generationVariant) ? payload.generationVariant : nextVariant);
        setEditHistory([]);
      }
      setNotice(
        payload.success
          ? nextVariant > 0
            ? "Different schedule generated and validated."
            : "Schedule generated and validated."
          : payload.error || "Generation was blocked."
      );
    } catch (error) {
      setNotice("Schedule generation failed before the server could respond.");
    } finally {
      setIsGenerating(false);
    }
  }

  function buildEditRequestParams(extra = {}) {
    const requestParams = new URLSearchParams({
      month: DEMO_MONTH,
      scenario: result?.scenario || scenario,
      variant: String(result?.generationVariant ?? generationVariant ?? 0),
    });
    const selectedStaffIds = result?.selectedStaffIds?.length ? result.selectedStaffIds : activeCustomStaffIds;
    if (selectedStaffIds?.length) requestParams.set("selectedStaffIds", selectedStaffIds.join(","));
    if (editHistory.length) requestParams.set("editHistory", JSON.stringify(editHistory));
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined && value !== null) requestParams.set(key, String(value));
    }
    return requestParams;
  }

  function resetGeneratedSchedule() {
    setResult(null);
    setNotice("");
    setGenerationVariant(0);
    setEditHistory([]);
  }

  function addCustomStaff(staffId) {
    const customization = fixture?.customization;
    if (!staffId || !customization?.enabled) return;
    setCustomSelectedStaffIds((currentIds) => {
      const sourceIds = currentIds.length ? currentIds : activeCustomStaffIds;
      if (sourceIds.includes(staffId) || sourceIds.length >= customization.maxDoctors) return sourceIds;
      return [...sourceIds, staffId];
    });
    resetGeneratedSchedule();
  }

  function removeCustomStaff(staffId) {
    const customization = fixture?.customization;
    if (!staffId || !customization?.enabled) return;
    setCustomSelectedStaffIds((currentIds) => {
      const sourceIds = currentIds.length ? currentIds : activeCustomStaffIds;
      if (sourceIds.length <= customization.minDoctors) return sourceIds;
      return sourceIds.filter((selectedStaffId) => selectedStaffId !== staffId);
    });
    resetGeneratedSchedule();
  }

  async function applyChatEdit(event) {
    event.preventDefault();
    if (!result?.schedule) return;
    setIsChatting(true);
    setNotice("");
    const requestParams = buildEditRequestParams({ message: chatMessage });
    try {
      const response = await fetch(`${apiBaseUrl}/medishift-demo/chat-modify?${requestParams.toString()}`);
      const payload = await parseJsonResponse(response);
      if (payload.success) {
        setResult(payload);
        setEditHistory(Array.isArray(payload.editHistory) ? payload.editHistory : editHistory);
        if (Number.isInteger(payload.generationVariant)) setGenerationVariant(payload.generationVariant);
      }
      setNotice(payload.message || payload.error || "Chat edit finished.");
    } catch (error) {
      setNotice("Chat edit failed before the server could respond.");
    } finally {
      setIsChatting(false);
    }
  }

  async function swapScheduleCells(from, to) {
    if (!result?.schedule) return;
    if (from.staff === to.staff && Number(from.dayIndex) === Number(to.dayIndex)) {
      setNotice("Choose two different doctors on the same day.");
      return;
    }
    if (Number(from.dayIndex) !== Number(to.dayIndex)) {
      setNotice("Drag shifts between doctors on the same day.");
      return;
    }

    setIsEditing(true);
    setNotice("");
    const requestParams = buildEditRequestParams({
      staff1: from.staff,
      staff2: to.staff,
      dayIndex: Number(from.dayIndex),
    });

    try {
      const response = await fetch(`${apiBaseUrl}/medishift-demo/swap-shift?${requestParams.toString()}`);
      const payload = await parseJsonResponse(response);
      if (payload.success) {
        setResult(payload);
        setEditHistory(Array.isArray(payload.editHistory) ? payload.editHistory : editHistory);
        if (Number.isInteger(payload.generationVariant)) setGenerationVariant(payload.generationVariant);
      }
      setNotice(payload.message || payload.error || "Shift swap finished.");
    } catch (error) {
      setNotice("Shift swap failed before the server could respond.");
    } finally {
      setIsEditing(false);
    }
  }

  async function exportExcel() {
    if (!canExport) return;
    setIsExporting(true);
    setNotice("");
    const requestParams = new URLSearchParams({
      month: DEMO_MONTH,
      scenario: result?.scenario || scenario,
      variant: String(result?.generationVariant || 0),
    });
    if (result?.selectedStaffIds?.length) requestParams.set("selectedStaffIds", result.selectedStaffIds.join(","));
    try {
      const response = await fetch(`${apiBaseUrl}/medishift-demo/export/excel?${requestParams.toString()}`);
      if (!response.ok) {
        const payload = await parseJsonResponse(response);
        throw new Error(payload.error || "Excel export failed.");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `medishift-website-demo-${DEMO_MONTH}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setNotice("Demo Excel workbook exported.");
    } catch (error) {
      setNotice(error.message || "Excel export failed.");
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <main className="medishift-demo-page">
      <section className="medishift-demo-toolbar">
        <div>
          <a className="medishift-back-link" href="/">
            Back to CodeCat
          </a>
          <h1>MediShift Demo</h1>
          <p>{fixture?.disclosure || "Demo data only."}</p>
        </div>
        <div className="demo-status-strip" aria-live="polite">
          <span className={`status-dot ${health?.status === "ok" ? "online" : "offline"}`} />
          <span>{health?.status === "ok" ? "Demo online" : "Checking demo"}</span>
        </div>
      </section>

      <DemoSetup
        fixture={visibleFixture}
        scenario={scenario}
        customSelectedStaffIds={activeCustomStaffIds}
        onScenarioChange={(nextScenario) => {
          setScenario(nextScenario);
          resetGeneratedSchedule();
        }}
        onAddStaff={addCustomStaff}
        onRemoveStaff={removeCustomStaff}
        onGenerate={generateSchedule}
        onExport={exportExcel}
        canExport={canExport}
        isGenerating={isGenerating}
        isExporting={isExporting}
        hasGeneratedSchedule={hasGeneratedSchedule}
      />

      <AvailabilityMatrix fixture={visibleFixture} resetKey={availabilityResetKey} />

      {notice && <div className="medishift-notice" role="status">{notice}</div>}

      <section className="medishift-results-stack">
        <ValidationPanel validation={result?.validation} />
        <ScheduleTable result={result} onSwap={swapScheduleCells} isEditing={isEditing} />
        <ChatEditor
          chatFeature={chatFeature}
          chatAvailable={chatAvailable}
          result={result}
          chatMessage={chatMessage}
          setChatMessage={setChatMessage}
          onSubmit={applyChatEdit}
          isChatting={isChatting}
        />
      </section>
    </main>
  );
}